/**
 * UploadDocumentModal — drag-drop a file, fill metadata, commit.
 *
 * Single file at a time in this patch. Multi-file is a polish item.
 *
 * Flow:
 *   1. User picks file → form is populated with sensible defaults
 *   2. User fills metadata (type, document date, anchor sub-selection, tags)
 *   3. Submit:
 *      a. Upload to MinIO via presigned URL (progress shown)
 *      b. Commit attachment row via API
 *   4. Modal closes on success
 */
import { useEffect, useState } from 'react';
import { Upload, FileText, X as XIcon } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Input, Textarea } from '@/components/ui/form-field';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { TagInput } from './tag-input';
import { AnchorPicker } from './anchor-picker';
import { describeUploadError, useSystemHealth } from '@/lib/system-health';
import {
  uploadFileToMinio,
  useAttachmentsList,
  useCreateDocument,
  type AttachmentAnchor,
  type DocumentType,
  type UploadProgress,
} from '@/lib/attachments-queries';

interface AnchorEntities {
  parts: { id: string; label: string }[];
  batches: { id: string; label: string }[];
  bankGuarantees: { id: string; label: string }[];
  inspectionEvents: { id: string; label: string }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When null, the user picks the project (or master entity) inside the picker. */
  projectId: string | null;
  /** Pre-fill the anchor (e.g., when opened from a specific entity surface). */
  defaultAnchor?: AttachmentAnchor;
  entities?: AnchorEntities;
  // Patch 2 — cross-project + master entity options
  projects?: { id: string; label: string }[];
  clients?: { id: string; label: string }[];
  suppliers?: { id: string; label: string }[];
  departments?: { id: string; label: string }[];
}

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  DFA: 'DFA',
  PO: 'Purchase order',
  SCHEDULE_OF_STORES: 'Schedule of Stores',
  SUPPLIER_INVOICE: 'Supplier invoice',
  CLIENT_INVOICE: 'Client invoice',
  BG_INSTRUMENT: 'BG instrument',
  INSPECTION_REPORT: 'Inspection report',
  LETTER: 'Letter',
  EMAIL_SCAN: 'Email scan',
  CHEQUE_COPY: 'Cheque copy',
  CERTIFICATE: 'Certificate',
  DRAWING: 'Drawing',
  AGREEMENT: 'Agreement',
  I_NOTE: 'I-Note',
  CRV: 'CRV',
  OTHER: 'Other',
};

const DOC_TYPES = Object.keys(DOC_TYPE_LABELS) as DocumentType[];

const MAX_FILE_SIZE = 250 * 1024 * 1024;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const EMPTY_ANCHOR: AttachmentAnchor = {
  projectId: null,
  projectItemId: null,
  orderBatchId: null,
  bankGuaranteeId: null,
  inspectionEventId: null,
  clientId: null,
  supplierId: null,
  masterDepartmentId: null,
};

export function UploadDocumentModal({
  open,
  onClose,
  projectId,
  defaultAnchor,
  entities,
  projects = [],
  clients = [],
  suppliers = [],
  departments = [],
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('OTHER');
  const [documentDate, setDocumentDate] = useState(todayIso());
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<AttachmentAnchor>(
    defaultAnchor ?? { ...EMPTY_ANCHOR, projectId: projectId ?? null },
  );
  // Patch 4 — optional supersede target. The picker only renders when the
  // current anchor + documentType has prior docs to replace; default null.
  const [supersedesId, setSupersedesId] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const createDoc = useCreateDocument();
  // Patch 3 — gives error messages context about whether storage is down
  const health = useSystemHealth();
  const storageDown = health.data?.storage.ok === false;

  // Patch 4 — candidates for the "Replaces" picker. We narrow to the same
  // anchor entity AND the same document type so the picker isn't a global
  // dump. The narrowest known anchor wins (sub-anchor > project > master).
  const candidateFilters = (() => {
    if (anchor.inspectionEventId) return { inspectionEventId: anchor.inspectionEventId };
    if (anchor.bankGuaranteeId) return { bankGuaranteeId: anchor.bankGuaranteeId };
    if (anchor.orderBatchId) return { orderBatchId: anchor.orderBatchId };
    if (anchor.projectItemId) return { projectItemId: anchor.projectItemId };
    if (anchor.projectId) return { projectId: anchor.projectId, projectLevelOnly: true };
    if (anchor.clientId) return { clientId: anchor.clientId };
    if (anchor.supplierId) return { supplierId: anchor.supplierId };
    if (anchor.masterDepartmentId) return { masterDepartmentId: anchor.masterDepartmentId };
    return null;
  })();
  const supersedeCandidatesQ = useAttachmentsList(
    candidateFilters
      ? {
          ...candidateFilters,
          kind: 'DOCUMENT',
          documentType,
          pageSize: 25,
        }
      : { pageSize: 0 },
  );
  const supersedeCandidates = (supersedeCandidatesQ.data?.items ?? []).filter(
    (d) => !d.deletedAt,
  );

  // When the anchor or doc type changes, drop a previously-selected supersede
  // target if it's no longer in the candidate set (e.g. user re-anchored
  // elsewhere).
  useEffect(() => {
    if (supersedesId && !supersedeCandidates.some((d) => d.id === supersedesId)) {
      setSupersedesId(null);
    }
  }, [supersedesId, supersedeCandidates]);

  const reset = () => {
    setFile(null);
    setDisplayName('');
    setDocumentType('OTHER');
    setDocumentDate(todayIso());
    setDescription('');
    setTags([]);
    setAnchor(
      defaultAnchor ?? { ...EMPTY_ANCHOR, projectId: projectId ?? null },
    );
    setSupersedesId(null);
    setProgress(null);
    setErrorMsg(null);
    setUploading(false);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const onFile = (f: File) => {
    setErrorMsg(null);
    if (f.size > MAX_FILE_SIZE) {
      setErrorMsg(`File is too large (${humanSize(f.size)}). Maximum is 250 MB.`);
      return;
    }
    setFile(f);
    // Pre-fill display name from filename (minus extension)
    const baseName = f.name.replace(/\.[^.]+$/, '');
    setDisplayName(baseName);
  };

  const canSubmit =
    !!file && displayName.trim().length > 0 && !!documentType && !!documentDate && !uploading;

  const submit = async (): Promise<void> => {
    if (!file || !canSubmit) return;
    setErrorMsg(null);
    setUploading(true);
    try {
      // 1. Upload to MinIO
      const { storageKey } = await uploadFileToMinio(file, (p) => setProgress(p));
      // 2. Commit
      await createDoc.mutateAsync({
        storageKey,
        fileSizeBytes: file.size,
        fileMimeType: file.type || 'application/octet-stream',
        displayName: displayName.trim(),
        documentType,
        documentDate,
        description: description.trim() || null,
        supersedesId,
        anchor,
        tags,
      });
      reset();
      onClose();
    } catch (err) {
      setErrorMsg(describeUploadError(err, storageDown));
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload document"
      size="md"
      lockBackdrop={uploading || createDoc.isPending}
      footer={
        <>
          <Button variant="tertiary" onClick={handleClose} disabled={uploading || createDoc.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit || createDoc.isPending}>
            {uploading || createDoc.isPending ? (
              <span className="inline-flex items-center gap-1">
                <Spinner className="h-3 w-3" />
                {progress && progress.percent < 100 ? `Uploading ${progress.percent}%` : 'Saving…'}
              </span>
            ) : (
              'Upload'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!file ? (
          <FileDropZone onFile={onFile} />
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-sm border border-border-default bg-base/40 px-3 py-2 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0 text-tertiary" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="truncate text-primary">{file.name}</div>
                <div className="text-xs text-secondary">{humanSize(file.size)} · {file.type || 'unknown type'}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFile(null)}
              disabled={uploading}
              aria-label="Remove file"
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-secondary disabled:opacity-50"
            >
              <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        )}

        {file ? (
          <>
            <Field label="Display name">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. BG-002 acceptance letter"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={documentType} onChange={(e) => setDocumentType(e.target.value as DocumentType)}>
                  {DOC_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {DOC_TYPE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Document date">
                <DmyDateInput
                  value={documentDate}
                  onChange={setDocumentDate}
                />
              </Field>
            </div>

            <AnchorPicker
              value={anchor}
              onChange={setAnchor}
              projectId={projectId}
              projects={projects}
              parts={entities?.parts ?? []}
              batches={entities?.batches ?? []}
              bankGuarantees={entities?.bankGuarantees ?? []}
              inspectionEvents={entities?.inspectionEvents ?? []}
              clients={clients}
              suppliers={suppliers}
              departments={departments}
              allowedKinds={projectId ? ['PROJECT'] : ['PROJECT', 'MASTER']}
            />

            <Field label="Description (optional)">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Anything worth noting about this document…"
              />
            </Field>

            {/* Patch 4 — Supersede picker. Only renders when prior docs of
              * the same type exist on the same anchor. Picking one creates a
              * version chain (anchor doc → this new one as v2). */}
            {supersedeCandidates.length > 0 ? (
              <Field label="Replaces (optional)">
                <Select
                  value={supersedesId ?? ''}
                  onChange={(e) => setSupersedesId(e.target.value || null)}
                >
                  <option value="">— Not a revision —</option>
                  {supersedeCandidates.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.displayName ?? '(untitled)'}
                      {d.documentDate ? ` · ${d.documentDate}` : ''}
                    </option>
                  ))}
                </Select>
                {supersedesId ? (
                  <p className="mt-1 text-[11px] text-tertiary">
                    This document will be added as the new current version. The previous
                    version stays accessible from the version history.
                  </p>
                ) : null}
              </Field>
            ) : null}

            <Field label="Tags">
              <TagInput value={tags} onChange={setTags} />
            </Field>
          </>
        ) : null}

        {errorMsg ? (
          <p className="text-xs text-status-danger">{errorMsg}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function FileDropZone({ onFile }: { onFile: (f: File) => void }) {
  const [dragging, setDragging] = useState(false);
  const onPick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) onFile(f);
    };
    input.click();
  };
  return (
    <div
      onClick={onPick}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPick();
        }
      }}
      className={
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed px-4 py-8 text-center transition-colors ' +
        (dragging
          ? 'border-accent bg-accent/5'
          : 'border-border-default hover:border-border-strong')
      }
    >
      <Upload className="h-6 w-6 text-tertiary" strokeWidth={1.5} />
      <div className="text-sm text-primary">Drop a file here or click to choose</div>
      <div className="text-xs text-secondary">PDF, images, Office docs, audio, video — up to 250 MB</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
